import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import JobTimer from './JobTimer';
import UserJobUpdateModal from './UserJobUpdateModal';
import QuickCloseJobModal from './QuickCloseJobModal';
import { WarrantyStatus } from './WarrantyStatus';
import { ApexReference, JobSearch } from './ApexReference';
import { JobDetailLauncher } from './JobDetailModal';
import { matchesJobSearch } from '../lib/jobReference';

export default function AllJobsView({ onEditJob, refreshKey }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingAppointment, setUpdatingAppointment] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [closingAppointment, setClosingAppointment] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(user.is_admin ? 'All' : 'Open');
  const [appliedFilter, setAppliedFilter] = useState(user.is_admin ? 'All' : 'Open');
  const [search, setSearch] = useState('');
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    fetchAppointments();
  }, [refreshKey]);

  useEffect(() => {
    if (user.is_admin) axios.get(`${API}/admin/users`).then(({ data }) => setStaff(data)).catch(() => toast.error('Unable to load assigned staff names'));
  }, [user.is_admin]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/${user.is_admin ? 'admin/appointments' : 'appointments'}`);
      // Sort by date and status
      const sorted = response.data.sort((a, b) => {
        if (a.status === 'Open' && b.status !== 'Open') return -1;
        if (a.status !== 'Open' && b.status === 'Open') return 1;
        return new Date(b.appointment_date) - new Date(a.appointment_date);
      });
      setAppointments(sorted);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (appointment) => {
    setUpdatingAppointment(appointment);
    setShowUpdateModal(true);
  };

  const handleClose = (appointment) => {
    setClosingAppointment(appointment);
    setShowCloseModal(true);
  };

  const openInWaze = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://waze.com/ul?q=${encoded}&navigate=yes`, '_blank');
  };

  const openInGoogleMaps = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  const getStatusColor = (status) => {
    const colors = {
      'Open': '#00FF00',
      'In Progress': '#FFD60A',
      'Waiting for Parts': '#FF6B00',
      'Waiting for Customer Update': '#00D4FF',
      'Waiting for Customer': '#00D4FF',
      'Closed': '#808080'
    };
    return colors[status] || '#FFFFFF';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Low': '#00FF00',
      'Medium': '#FFD60A',
      'High': '#FF6B00',
      'Urgent': '#FF006E'
    };
    return colors[priority] || '#FFFFFF';
  };

  if (loading) {
    return (
      <div className="retro-panel">
        <div className="text-center" style={{ color: '#FF6B00' }}>
          [LOADING JOBS...]
        </div>
      </div>
    );
  }

  // Filter appointments based on applied filter (case-insensitive)
  const filteredAppointments = (appliedFilter === 'All' || appliedFilter === 'All Jobs'
    ? appointments 
    : appointments.filter(apt => {
        const aptStatus = (apt.status || '').trim();
        return aptStatus.toLowerCase() === appliedFilter.toLowerCase();
      })).filter(job => matchesJobSearch(job, search));
  
  const handleApplyFilter = () => {
    setAppliedFilter(selectedFilter);
  };

  return (
    <>
      <div className="retro-panel">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h2 className="text-3xl font-bold glow-text" style={{ color: '#FF6B00' }}>
            [JOBS] - TOTAL: {filteredAppointments.length}
          </h2>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[var(--accent)] font-bold">FILTER:</label>
            <select
              data-testid="jobs-status-filter"
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)] font-bold"
            >
              <option value="All">All Jobs</option>
              <option value="Open">Open</option>
              <option value="Scheduled">Scheduled</option>
              <option value="In Progress">In Progress</option>
              <option value="Waiting for Customer">Waiting for Customer</option>
              <option value="Waiting for Parts">Waiting for Parts</option>
              <option value="Completed">Completed</option>
              <option value="Closed">Closed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <button
              onClick={handleApplyFilter}
              data-testid="jobs-filter-apply"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold border-2 border-green-600"
            >
              [APPLY]
            </button>
          </div>
        </div>

        <JobSearch scope="jobs" value={search} onChange={setSearch} />
        {filteredAppointments.length === 0 ? (
          <div className="text-center text-lg" style={{ color: '#ffffff' }}>
            [NO {appliedFilter.toUpperCase()} JOBS]
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="border-2 p-3 md:p-4"
                style={{ 
                  borderColor: getStatusColor(appointment.status), 
                  background: `${getStatusColor(appointment.status)}10`
                }}
              >
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex-1">
                    <div className="mb-3"><ApexReference job={appointment} scope="jobs" /></div>
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-2 gap-2">
                      <div className="text-xl md:text-2xl font-bold" style={{ color: '#ffffff' }}>
                        {appointment.customer_name}
                      </div>
                      {appointment.assigned_to_user_id === user.id && <JobTimer 
                        appointment={appointment} 
                        onUpdate={fetchAppointments}
                      />}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                      {user.is_admin && <div data-testid={`job-assignee-${appointment.id}`}><span style={{ color: '#FF6B00' }}>Assigned to:</span> <span className="text-white">{staff.find(member => member.id === appointment.assigned_to_user_id)?.username || 'Unassigned / unavailable'}</span></div>}
                      {appointment.customer_email && <div data-testid={`job-email-${appointment.id}`} className="break-words text-white">Email: {appointment.customer_email}</div>}
                      {appointment.customer_mobile && <div data-testid={`job-mobile-${appointment.id}`} className="break-words text-white">Phone: {appointment.customer_mobile}</div>}
                      <div>
                        <span style={{ color: '#FF6B00' }}>Date:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.appointment_date}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Time:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.start_time} - {appointment.end_time}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Job Type:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.job_type}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Location:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.location}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Priority:</span>{' '}
                        <span style={{ color: getPriorityColor(appointment.priority), fontWeight: 700 }}>
                          {appointment.priority}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Status:</span>{' '}
                        <span style={{ color: getStatusColor(appointment.status), fontWeight: 700 }}>
                          {appointment.status}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div style={{ color: '#FF6B00' }}>Description:</div>
                      <div style={{ color: '#ffffff' }}>{appointment.description}</div>
                    </div>
                    <WarrantyStatus appointment={appointment} />

                    {/* Map Navigation Buttons */}
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => openInWaze(appointment.location)}
                        className="retro-btn retro-btn-yellow flex-1"
                      >
                        [WAZE →]
                      </button>
                      <button
                        onClick={() => openInGoogleMaps(appointment.location)}
                        className="retro-btn retro-btn-yellow flex-1"
                      >
                        [GOOGLE MAPS →]
                      </button>
                    </div>

                    {appointment.actual_start_time && (
                      <div className="mt-2 p-2 border" style={{ borderColor: '#00FF00', backgroundColor: '#001a00' }}>
                        <span style={{ color: '#00FF00' }}>
                          ⏱️ Actual Work Time: {appointment.actual_start_time} - {appointment.actual_end_time}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <JobDetailLauncher appointment={appointment} onUpdate={fetchAppointments} scope="jobs" />
                    <button
                      onClick={() => user.is_admin ? onEditJob(appointment) : handleEdit(appointment)}
                      data-testid={user.is_admin ? `job-admin-edit-${appointment.id}` : `job-update-${appointment.id}`}
                      className="retro-btn w-full"
                    >
                      {user.is_admin ? '[EDIT JOB]' : '[UPDATE JOB]'}
                    </button>
                    {appointment.status !== 'Closed' && appointment.assigned_to_user_id === user.id && (
                      <button
                        onClick={() => handleClose(appointment)}
                        data-testid={`job-close-${appointment.id}`}
                        className="retro-btn retro-btn-green w-full"
                      >
                        [CLOSE JOB]
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showUpdateModal && updatingAppointment && (
        <UserJobUpdateModal
          appointment={updatingAppointment}
          onSuccess={() => {
            setShowUpdateModal(false);
            setUpdatingAppointment(null);
            fetchAppointments();
          }}
          onClose={() => {
            setShowUpdateModal(false);
            setUpdatingAppointment(null);
          }}
        />
      )}

      {showCloseModal && closingAppointment && (
        <QuickCloseJobModal
          appointment={closingAppointment}
          onSuccess={() => {
            setShowCloseModal(false);
            setClosingAppointment(null);
            fetchAppointments();
          }}
          onClose={() => {
            setShowCloseModal(false);
            setClosingAppointment(null);
          }}
        />
      )}
    </>
  );
}
